<script setup lang="ts">
// TODO :: TASK-2103 - Should be promoted to a molecule
import { computed, ref, useTemplateRef, watch } from 'vue';
import { storeToRefs } from 'pinia';
import OverlayPanel from 'primevue/overlaypanel';
import type { Discount, CartAnimal } from '../../../interfaces';
import { useAToast } from '@Atoms';
import { applyShelterCurrency } from '@Helpers';
import { AddDiscountItemService, DeleteItemDiscount } from '../../../services';
import useShoppingCartStore from '../../../store';

const emit = defineEmits<{
  onShoppingCartDiscountSelectorOpening: [];
}>();

const props = withDefaults(
  defineProps<{
    animal: CartAnimal;
    disabled?: boolean;
    isEditingAnimals?: boolean;
    label?: string;
  }>(),
  {
    disabled: false,
    label: 'cart.summary.promo',
  }
);

const store = useShoppingCartStore();
const { cartData, discounts } = storeToRefs(store);

const { notify, TOAST_TYPES } = useAToast();

const TOAST_ERROR = {
  message: 'cart.toast.medicalCosts.error.message',
  title: 'cart.toast.medicalCosts.error.title',
  type: TOAST_TYPES.ERROR,
};

const promoBlockedOverlay = useTemplateRef<InstanceType<typeof OverlayPanel> | null>('promoBlockedOverlay');

const isOpen = ref(false);

const selectedDiscount = computed<Discount | undefined>(() =>
  discounts.value.find(({ code }: Discount) => code === selectedCode.value)
);

const selectedCodeValue = computed<number>(() => selectedDiscount.value?.amount ?? 0);

const isPercentDiscount = computed(() => selectedDiscount.value?.discountType === 'percent');

const discountDisplay = computed(() => {
  const value = selectedCodeValue.value;

  if (value <= 0) return '';
  return isPercentDiscount.value ? `${value}%` : applyShelterCurrency(value).replace(/\$([\d,.]+)/, '$($1)');
});

watch(
  () => isOpen.value,
  (nowOpen, wasOpen) => nowOpen && !wasOpen && emit('onShoppingCartDiscountSelectorOpening')
);

const selectedCode = computed({
  get: () => props.animal?.discount_code ?? null,
  set: (newCode: string | null) => {
    const oldCode = props.animal?.discount_code;
    const itemId = props.animal?._id;
    if (!itemId) return;

    const isRemoving = oldCode && !newCode;
    if (isRemoving) {
      DeleteItemDiscount({
        code: oldCode,
        item_id: itemId,
        target_item: 'animals',
      })
        .then(({ data }) => (cartData.value = data))
        .catch(() => notify(TOAST_ERROR));
      return;
    }

    if (!newCode) return;

    AddDiscountItemService({
      code: newCode,
      item_id: itemId,
      target_item: 'animals',
    })
      .then(({ data }) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  },
});

function togglePromoBlockedOverlay(event: Event) {
  promoBlockedOverlay.value?.toggle(event);
}
</script>

<template>
  <div class="flex items-center justify-between gap-2">
    <div class="relative flex items-center gap-1">
      <label
        v-if="selectedCode"
        class="absolute -top-2 left-0 z-10 text-xs text-gray-500"
      >
        {{ $t(label) }}
      </label>

      <div :class="[{ 'cursor-help': disabled }, 'relative']">
        <AFieldDropdown
          v-model="selectedCode"
          v-model:is-open="isOpen"
          class="pt h-8 w-fit"
          clearIcon="pi pi-trash"
          clearIconStyle="text-red-600 items-center text-xs static !flex mt-0"
          customClass="border-none shadow-none ring-0 outline-none bg-transparent"
          customDropdownIconStyle="af-link text-xs font-semibold"
          customInputStyle="af-link pl-0 pr-1 py-0"
          customTriggerStyle="text-gray-700 !pr-0.5"
          id="promo"
          optionLabel="code"
          optionValue="code"
          :disabled
          :dropdownIcon="selectedCode ? '!hidden' : 'pi pi-plus'"
          :options="discounts"
          :placeholder="$t(label)"
        />

        <div
          v-if="disabled"
          class="absolute inset-0"
          @click="togglePromoBlockedOverlay($event)"
          @mouseenter="togglePromoBlockedOverlay($event)"
          @mouseleave="togglePromoBlockedOverlay($event)"
        />

        <OverlayPanel
          v-if="disabled"
          class="max-w-[280px] text-sm"
          ref="promoBlockedOverlay"
        >
          <p class="text-surface-700 dark:text-surface-300 p-0">
            {{ $t('cart.summary.promoBlockedByCustomDiscount') }}
          </p>
        </OverlayPanel>
      </div>
    </div>

    <label
      v-if="discountDisplay"
      :class="[
        'text-sm font-medium text-gray-700',
        isEditingAnimals ? 'pr-12' : '',
        selectedCodeValue > 0 ? 'text-red-600' : 'text-gray-500',
      ]"
    >
      {{ discountDisplay }}
    </label>
  </div>
</template>
